/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */


import Config from 'components/startingPoints/relationshipGraph/Config.js';
import SmartsRecommenderService from './SmartsRecommenderService';
import RelationshipBaseRequestHandler, { ABANDONED_RELATIONSHIP_REQUEST } from './RelationshipBaseRequestHandler';
import RelationshipScopeRequestHandler from './RelationshipScopeRequestHandler';
import ShowRelationshipRequestHandler from './ShowRelationshipRequestHandler';
import StartingPointsRequestError from './StartingPointsRequestError';
import { applyTransforms, findNodesFromCache, findDirectLink, formatLinkStrength } from 'components/startingPoints/relationshipGraph/RelationshipUtil.js';

/**
 * This class fetches relationship digram raw data. If enabled, it caches
 * a hard-coded number of entries of fetched data for each target ID.
 * The cache is cleared upon a new request made for a new source model ID.
 *
 * @extends SmartsRecommenderService
 */
class SmartsRelationshipStrengthService extends SmartsRecommenderService {

	constructor(options) {
		super(options);
		this._dataCache = null;
	}

	/**
	* @param {JSONObject} requestParams Query specifications (request payload)
	* @param {JSONObject} context Context of the query, such as the associatd module API
	*/
	getRelationshipStrength(requestParams, context) {
		context.targetModel = context.model.getTargetModel();
		this.logger.debug('getRelationshipStrength');
		const requestOptions = {
			groupingNode: requestParams.showConcepts === true,
			lowestConcept: true
		};
		this._setTimeout(requestOptions, context);
		return this.fetchData(requestParams, requestOptions , context)
			.catch( this._handleError.bind(this) );
	}

	_setTimeout(requestOptions, context){
		if(context.model && context.model.allowLongRunningRequest && !context.model.allowLongRunningRequest()){
			requestOptions.timeout = Config.max_request_waiting_time;
		}
	}

	//handle error specifically, if cannot handel, then call parent's _handleError
	_handleError(ajaxResponse) {
		if (ajaxResponse) {
			//handle specifically
			//1. handle abandoned request
			if(ajaxResponse.code === ABANDONED_RELATIONSHIP_REQUEST) {
				const abandonedError = StartingPointsRequestError.getErrorObject(ABANDONED_RELATIONSHIP_REQUEST);
				abandonedError.abandonedRequest = ajaxResponse.abandonedRequest;
				throw abandonedError;
			}

			//
			const error = ajaxResponse.data && ajaxResponse.data.errors && ajaxResponse.data.errors[0];
			if(error) {
				const { code } = error;//External HTTP request Error
				if(code) {
					const handledError = StartingPointsRequestError.getErrorObject(code);//Get Internal Error
					if(handledError){
						throw handledError;
					}
				}
			}
		}
		//if cannot handel,  call parent's _handleError
		super._handleError(ajaxResponse);

	}

	/**
	 * Resolve data from data cache as long as cache is enabled and data is available.
	 * Otherwise, fetch data via Ajax call made to /relationship endpoint
	 *
	 * @param{object} querySpec	Query specifications (request payload)
	 * @param{object} requestOptions request opions, such as { timeout : 120000, xxx: xxxx, ...}
	 * @param{object} context	context such as Model APIs
	 * @return {promise}
	 */
	fetchData(querySpec, requestOptions, context) {
		// Regardless of we go to network or using cache, if it is the case that `diagramData` is used,
		// then we need to access `diagramData` inside `context`, so anyway set `diagramData` to context,
		// if we `_needDiagramData` is true
		this._setDiagramData(context);
		if(Config.use_cached_data){
			let cachedData = null;
			const targetRawData = this._getCachedRawData(querySpec, context);
			const isFilterStrengthFeatureEnabled = context.model.getIsFilterStrengthFeatureEnabled();

			if(targetRawData){
				if (!isFilterStrengthFeatureEnabled) {
					// R2 behavior: return cached raw data
					cachedData = targetRawData;
				} else {
					// R3 behavior
					const transformOptions = context && context.transformOptions;
					context.rawData = targetRawData;
					if(!this._ignoreCacheData(context.rawData, transformOptions, querySpec)){
						cachedData = targetRawData;
					}
				}
			}
			if(cachedData){
				if (isFilterStrengthFeatureEnabled) {
					cachedData = applyTransforms(context);
				}
				return Promise.resolve(cachedData);
			}
		}
		return this._sendRequest(querySpec, requestOptions, context);
	}

	_sendRequest(querySpec, requestOptions, context){
		return	this._getRequestHandler(context)._sendRequest(querySpec, requestOptions, context, this);
	}

	/**
	 * Provide the right request handler for the given context of actions
	 * TODO: Currently, the base service of this servie has embedded logic to handle request
	 * In the future, we should do more refacotoring to make the flow clear and more tiered.
	 */
	_getRequestHandler(context){
		if(context && context.transformOptions) {
			const transformOptions = context.transformOptions;
			if(transformOptions.lastAddedFields || transformOptions.scopedFields){
				return RelationshipScopeRequestHandler;
			} else if(transformOptions.lastShowRelationshipsField) {
				return ShowRelationshipRequestHandler;
			} else {
				return RelationshipBaseRequestHandler;
			}
		}
		return RelationshipBaseRequestHandler;
	}

	/**
	 * Determine if we should ignore the cached data we found
	 * @param {object} cache
	 * @param {object} transformOptions
	 * @param {object} querySpec
	 */
	_ignoreCacheData(cache, transformOptions, querySpec){
		if(transformOptions.lastAddedFields || transformOptions.scopedFields || transformOptions.lastShowRelationshipsField) {
			if(cache) {
				if(transformOptions.lastAddedFields) {
					const fieldsToSearch = transformOptions.lastAddedFields;
					const result = findNodesFromCache( fieldsToSearch, cache );
					//if any of the searched fields is not found, then we ignore cache
					if(result.unfoundNodeIds.length > 0) {
						transformOptions.unfoundNodes = result.unfoundNodeIds;
						return true;
					} else {
						return false;
					}
				} else if(transformOptions.scopedFields) {
					return true;
				} else if(transformOptions.lastShowRelationshipsField) {
					const lastShowRelationshipsField = transformOptions.lastShowRelationshipsField;
					const { from, to } = lastShowRelationshipsField;
					// we first find the direct link from the raw data of current target
					// if we cannot find it, we then find it in the raw data of `from` and `to`
					// when they are the target, if we found it then we should not ignore raw cache
					// if we cannot find, then we should go to network
					const result = findDirectLink(from, to, cache) || this._findDirectLinkInRawDataOfTwoNodes(from, to, querySpec.sourceModuleId);
					if(result) {
						lastShowRelationshipsField.relationship = {
							toNode: result.nodeTwo,
							link: result.link
						};
						return false;
					} else {
						return true;
					}
				}
			} else {
				return true;
			}
		}
		return false;
	}

	/**
	 * Find direct link between raw data of the two nodes(i.e., the raw data when they are the target)
	 * @param {string} from - from field id
	 * @param {string} to - to field id
	 * @param {string} sourceModuleId - sourceModuleId
	 * @returns {Object} {nodeOne, nodeTwo, link: link} - nodeOne is the node for`from` field, nodeTwo is the node for`to` field
	 */
	_findDirectLinkInRawDataOfTwoNodes(nodeOne, nodeTwo, sourceModuleId) {
		let foundDirectRelationship = null;
		if(this._dataCache && this._dataCache.length) {
			[nodeOne, nodeTwo].find((field) => {
				const dataCacheEntry = this._dataCache.find((entry)=> entry.sourceModuleId === sourceModuleId && entry.targetId === field);
				const rawData = dataCacheEntry && dataCacheEntry.rawData;
				const result = rawData && findDirectLink(nodeOne, nodeTwo, rawData);
				if(result) {
					foundDirectRelationship = result;
					return true;
				} else {
					return false;
				}
			});
		}
		return foundDirectRelationship;
	}

	/**
	 * @private
	 * set `diagramData` to context if needed
	 * @param {object} context - contains information for data transformation
	 */
	_setDiagramData(context) {
		if (context && context.transformOptions && this._needDiagramData(context.transformOptions)) {
			context.diagramData = context.targetModel && context.targetModel.getDiagramData();
		}
	}

	/**
	 * get cached raw data
	 * @param {object} querySpec - querySpec
	 */
	_getCachedRawData(querySpec, context){
		const { sourceModuleId, targetFieldIDForExpression: targetId, maxInfluencerFields } = querySpec;
		if( this._dataCache && this._dataCache.length >0) {
			const targetData = this._dataCache.find(
				(entry)=> entry.targetId === targetId &&
					entry.sourceModuleId === sourceModuleId &&
					(this._isAddFieldOrScopeFeatureEnabled(context.model) ? true : entry.maxInfluencerFields === maxInfluencerFields)
			);
			return targetData && targetData.rawData;
		}
		return null;
	}

	getDataCache() {
		return this._dataCache;
	}

	/**
	 * Determine if the `diagramData` is needed
	 * @param {object} transformOptions
	 */
	_needDiagramData(transformOptions) {
		if (transformOptions.deletedFields || transformOptions.lastAddedFields || transformOptions.lastShowRelationshipsField) {
			return true;
		}
	}

	_isAddFieldOrScopeFeatureEnabled(relationpVM) {
		if (relationpVM) {
			return relationpVM.isAddFieldFeatureEnabled() || relationpVM.isEditScopeFeatureEnabled();
		} else {
			return false;
		}
	}

	/**
	 * Add fetcehd data to data cache if cache is enabled
	 *
	 * @private
	 */
	_addRawDataToCache(querySpec, response, context) {
		if(!Config.use_cached_data){
			return;
		}
		if(this._dataCache == null){
			this.initDataCache();
		}
		//do format relationship strength before we add the data to cache
		this._formatRelationshipStrengthFormat(response);

		if(querySpec.sourceModuleId && querySpec.targetFieldIDForExpression){
			this.removeDatedEntriesFromCache( querySpec.sourceModuleId, querySpec.targetFieldIDForExpression );
			this._removeOldestEntryWhenMaxReaches(context);
			const cacheEntry = { sourceModuleId: querySpec.sourceModuleId, dataSourceId: context.dataSourceId, targetId: querySpec.targetFieldIDForExpression, rawData: response };
			if(!this._isAddFieldOrScopeFeatureEnabled(context.model)) {
				cacheEntry.maxInfluencerFields = querySpec.maxInfluencerFields;
			}
			this._dataCache.push(cacheEntry);
		}
	}

	_formatRelationshipStrengthFormat(rawData) {
		const linkDataset = rawData && rawData.relationshipStrength && rawData.relationshipStrength.results && rawData.relationshipStrength.results.length ? rawData.relationshipStrength.results[0].relationships:[];
		linkDataset.forEach((link) => {
			formatLinkStrength(link);
		});
	}

	initDataCache() {
		this._dataCache = [];
	}

	/**
	 * Remove entries that contain the passed source module Id
	 * if source
	 * @param{string} sourceModuleId
	 */
	removeEntriesFromCache(sourceModuleId) {
		if(this._dataCache && this._dataCache.length >0){
			let index = this._dataCache.length;
			while (index--) {
				if(this._dataCache[index].sourceModuleId === sourceModuleId){
					this._dataCache.splice(index , 1);
				}
			}
		}
	}

	/**
	 * Remove the cached entries that have the passed in source module ID, targetID
	 * @param{string} sourceModuleId source module ID
	 * @param {string} tagetId Target ID
	 */
	removeDatedEntriesFromCache(sourceModuleId, targetId) {
		if(this._dataCache && this._dataCache.length >0){
			let index = this._dataCache.length;
			let entry;
			while (index--) {
				entry = this._dataCache[index];
				if(entry.sourceModuleId === sourceModuleId
					&& entry.targetId === targetId
				){
					this._dataCache.splice(index , 1);
				}
			}
		}
	}

	_removeOldestEntryWhenMaxReaches(context) {
	/*We remove the oldest entry if before adding a new entry, the cache length reaches limits*/
		if(this._dataCache && this._dataCache.length === Config.max_cached_data_pool_length){
			const removedDataCache = this._dataCache.shift();
			const relationshipVM = context.model;
			relationshipVM.destroyTargetModel(removedDataCache.dataSourceId, removedDataCache.targetId);
		}
	}

	_handlePredictStatus(smartsResponse) {
		const predictStatus = smartsResponse.relationshipStrength && smartsResponse.relationshipStrength.predictStatus ?smartsResponse.relationshipStrength.predictStatus : null;
		if (predictStatus) {
			if (predictStatus.errors && predictStatus.errors.length) {
				this.logger.error('Predict error:' + JSON.stringify(predictStatus.errors));
			}
			if (predictStatus.warnings && predictStatus.warnings.length) {
				this.logger.error('Predict warning:' + JSON.stringify(predictStatus.warnings));
			}
		}
	}
}

export default SmartsRelationshipStrengthService;



// WEBPACK FOOTER //
// ./src/js/util/SmartsRelationshipStrengthService.js